import { useMemo } from 'react';
import {
  FlattenedGroupRow,
  LineItemRow,
  PlacementAnalyticsRow,
  TopLevelRow,
  isExploreRow,
  isFlattenedGroup
} from './post-optimization-constants';
import {
  calculateBaseAttractiveness,
  calculateESS,
  calculateFinalAllocation,
  calculateGroupAverageAttractiveness,
  calculateRawAllocation,
  calculateWeightedShare,
  computeWinsorBounds,
  applyProportionalBudgetReallocation,
  winsorizeMetric
} from './post-optimization-calculations';

const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0);

/**
 * Apply all allocation calculations to hierarchical table data
 *
 * This hook performs all calculations per group:
 * 1. Allocation Metric (from API)
 * 2. Winsorized Metric
 * 3. Base Attractiveness
 * 4. ESS (Bayesian Shrinkage)
 * 5. Weighted Share
 * 6. Raw Allocation
 * 7. Final Allocation
 *
 * @param hierarchicalData - The hierarchical table structure from the data transform
 * @param allocationCriteria - The selected metric (e.g., 'incremental_roas')
 * @returns Updated hierarchical data with all calculated fields populated
 */
export const useAllocationCalculations = (
  hierarchicalData: TopLevelRow[],
  allocationCriteria: keyof PlacementAnalyticsRow
): TopLevelRow[] =>
  useMemo(() => {
    const calculatedData = hierarchicalData.map(group => {
      if (isExploreRow(group)) {
        return { ...group, finalAllocation: group.mustExclude ? 0 : group.budget };
      }

      const placements = isFlattenedGroup(group) ? [group] : group.subRows;

      const groupBudget = group.budget;

      const allocationMetrics = placements.map(p => {
        const metricValue = p.apiData?.[allocationCriteria];
        return typeof metricValue === 'number' ? metricValue : ('no data' as const);
      });

      const mustExcludeFlags = placements.map(p => p.mustExclude || false);
      const winsorBounds = computeWinsorBounds(allocationMetrics, mustExcludeFlags);
      const winsorizedMetrics = allocationMetrics.map(metric =>
        winsorizeMetric(metric, winsorBounds)
      );

      const baseAttractivenessValues = winsorizedMetrics.map(winsorized =>
        calculateBaseAttractiveness(winsorized, allocationCriteria)
      );

      const groupAvgAttractiveness = calculateGroupAverageAttractiveness(
        baseAttractivenessValues,
        mustExcludeFlags
      );

      const essValues = placements.map((p, idx) => {
        const totalImpressions = p.apiData?.total_impressions || 0;
        return calculateESS(
          baseAttractivenessValues[idx],
          totalImpressions,
          groupAvgAttractiveness
        );
      });

      const eligibleEssValues = essValues.filter((_, idx) => !mustExcludeFlags[idx]);
      const eligibleEssSum = sum(eligibleEssValues);
      const weightedShares = essValues.map((ess, idx) =>
        calculateWeightedShare(ess, mustExcludeFlags[idx], eligibleEssSum, eligibleEssValues.length)
      );

      const mustIncludeFlags = placements.map(p => p.mustInclude || false);
      const rawAllocations = weightedShares.map((share, idx) =>
        calculateRawAllocation(share, groupBudget, mustIncludeFlags[idx])
      );

      const totalRawAllocation = sum(rawAllocations);
      const finalAllocations = rawAllocations.map(raw =>
        calculateFinalAllocation(raw, groupBudget, totalRawAllocation)
      );

      const updatedPlacements = placements.map((placement, idx) => ({
        ...placement,
        allocationMetric: allocationMetrics[idx],
        winsorizedMetric: winsorizedMetrics[idx],
        baseAttractiveness: baseAttractivenessValues[idx],
        ess: essValues[idx],
        weightedShare: weightedShares[idx],
        rawAllocation: rawAllocations[idx],
        finalAllocation: finalAllocations[idx]
      }));

      if (isFlattenedGroup(group)) {
        return updatedPlacements[0] as FlattenedGroupRow;
      }

      return {
        ...group,
        subRows: updatedPlacements as LineItemRow[]
      };
    });

    return applyProportionalBudgetReallocation(calculatedData);
  }, [hierarchicalData, allocationCriteria]);
